// Utilitaire pour charger la configuration centralisée depuis domain-config.json
import domainConfigData from '../../domain-config.json';

export interface DomainEnvironmentConfig {
  PUBLIC_API_URL: string;
  PUBLIC_ENABLE_FEATURE_IA_SUGGESTION: string;
  DECLARATION_SAV_URL: string;
  PRO_URL_V2?: string;
  HOME_DIPROPNEU_URL?: string;
  SOUSCRIPTION_DIPROPNEU_URL?: string;
  ENABLE_SENTRY?: string;
  GESTION_SAV_URL?: string;
  STRIPE_PUBLIC_KEY?: string;
  STRIPE_TEST_PUBLIC_KEY?: string;
  ADMIN_URL?: string;
  AWS_S3_FILES_URL?: string;
}

export interface DomainConfig {
  title: string;
  faviconUrl: string;
  router: string;
  brand: string;
  environment: DomainEnvironmentConfig;
}

export interface DomainConfigData {
  domains: Record<string, DomainConfig>;
  defaults: DomainConfig;
  routerNames: Record<string, string>;
  /**
   * Label de hostname → clé de `domains` (cf. resolveLabelledHostname).
   * Optionnel : un label absent est cherché tel quel comme clé de `domains`.
   */
  labelDomains?: Record<string, string>;
}

// Configuration centralisée
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const domainConfig: DomainConfigData = domainConfigData as DomainConfigData;

/**
 * Obtient la configuration pour un hostname donné
 */
export function getDomainConfig(hostname?: string): DomainConfig {
  if (!hostname) {
    hostname = window.location.hostname;
  }

  const port = window.location.port;
  const hostnameWithPort = port ? `${hostname}:${port}` : hostname;

  // Essayer avec le port d'abord, puis sans
  const config = domainConfig.domains[hostnameWithPort] ||
                 domainConfig.domains[hostname] ||
                 resolveLabelledHostname(hostname) ||
                 resolveUpsellLandingWildcard(hostname) ||
                 domainConfig.defaults;

  console.log('[Domain Config] Hostname:', hostnameWithPort, 'Config:', config);

  return config;
}

/**
 * Résout les hostnames « à label » : `<label>--<instance>.<domaine>`.
 *
 * C'est la convention des environnements éphémères (une instance = un jeu de
 * hostnames publics, un par front) : `upsell--ab12cd.projet.exemple.com`,
 * `admin--ab12cd.projet.exemple.com`… Le hostname exact est inconnu de
 * `domains` — sans ce chemin, tous les fronts d'une instance retombent sur
 * `defaults` (= routeur `sav`) et affichent la gestion SAV quelle que soit l'URL.
 *
 * Le **label** (avant `--`) identifie le front. Il est résolu vers une entrée
 * `domains` existante via `labelDomains` (sinon : le label lui-même comme clé),
 * dont on reprend routeur / titre / favicon / brand. Les URLs d'environnement
 * (PUBLIC_API_URL, URLs inter-fronts) sont surchargées séparément au runtime
 * par `window.__RUNTIME_ENV__` (cf. getEnvironmentConfig).
 *
 * Aucun hostname de prod ne contient `--` dans son premier label : ce chemin
 * est inerte hors environnement éphémère.
 */
function resolveLabelledHostname(hostname: string): DomainConfig | null {
  const firstLabel = hostname.split('.')[0];
  const separator = firstLabel.indexOf('--');
  if (separator <= 0) return null;

  const label = firstLabel.slice(0, separator);
  const domainKey = domainConfig.labelDomains?.[label] || label;
  return domainConfig.domains[domainKey] || null;
}

/**
 * Résout les hostnames <vendor>.<env?>.extension-garantie.fr (landing upsell publique).
 * Dev : *.devdavid.extension-garantie.fr → entrée `upsell-landing-wildcard-dev`
 * Preprod : *.preprod.extension-garantie.fr → entrée `upsell-landing-wildcard-preprod`
 * Prod : *.extension-garantie.fr → entrée `upsell-landing-wildcard-prod`
 * Le hostname exact racine (sans subdomain vendeur) → page "lien invalide".
 */
function resolveUpsellLandingWildcard(hostname: string): DomainConfig | null {
  if (!hostname.endsWith('.extension-garantie.fr') && hostname !== 'extension-garantie.fr') {
    return null;
  }
  if (hostname.endsWith('.devdavid.extension-garantie.fr') || hostname === 'devdavid.extension-garantie.fr') {
    return domainConfig.domains['upsell-landing-wildcard-dev'] || null;
  }
  if (hostname.endsWith('.preprod.extension-garantie.fr') || hostname === 'preprod.extension-garantie.fr') {
    return domainConfig.domains['upsell-landing-wildcard-preprod'] || null;
  }
  return domainConfig.domains['upsell-landing-wildcard-prod']
    || domainConfig.domains['upsell-landing-wildcard'] // fallback legacy key
    || null;
}

/**
 * Obtient uniquement la configuration d'environnement.
 *
 * Surcharge runtime : si window.__RUNTIME_ENV__ est défini (généré par
 * scripts/transformRuntimeEnv.js — vide en prod), ses champs l'emportent
 * CHAMP PAR CHAMP sur la config du domaine. Permet à un environnement
 * single-tenant (ex. une tâche Hive) de pointer PUBLIC_API_URL et les URLs
 * inter-fronts sur SES ports, sans rebuild ni édition de domain-config.json.
 * Absent/vide => aucun override, comportement identique à avant (prod intacte).
 */
export function getEnvironmentConfig(hostname?: string): DomainEnvironmentConfig {
  const base = getDomainConfig(hostname).environment;
  const override =
    typeof window !== 'undefined' ? (window as unknown as { __RUNTIME_ENV__?: Record<string, string> }).__RUNTIME_ENV__ : undefined;
  if (!override || typeof override !== 'object') {
    return base;
  }
  const merged: DomainEnvironmentConfig = { ...base };
  for (const key of Object.keys(override)) {
    const val = override[key];
    if (val !== undefined && val !== null && val !== '') {
      (merged as unknown as Record<string, string>)[key] = val;
    }
  }
  return merged;
}

/**
 * Obtient le nom du router pour un hostname
 */
export function getRouterName(hostname?: string): string {
  return getDomainConfig(hostname).router;
}

/**
 * URL de la plateforme upsell du MÊME environnement que le hostname courant :
 * première entrée `router === 'upsell'` qui partage le même PUBLIC_API_URL.
 * Retourne null si introuvable (environnement sans plateforme upsell déclarée).
 * Utilisé pour rediriger les internes « upsell only » qui atterrissent sur l'admin.
 */
export function getUpsellPlatformUrl(hostname?: string): string | null {
  const currentApi = getEnvironmentConfig(hostname).PUBLIC_API_URL;
  for (const [domain, config] of Object.entries(domainConfig.domains)) {
    if (config.router !== 'upsell') continue;
    if (config.environment?.PUBLIC_API_URL !== currentApi) continue;
    // Les clés locales sans point (alias dev) ne font pas des URLs valides
    if (!domain.includes('.') && !domain.includes('localhost')) continue;
    const protocol = domain.startsWith('localhost') || domain.startsWith('127.0.0.1') ? 'http' : 'https';
    return `${protocol}://${domain}`;
  }
  return null;
}

// Fonction getRouterKey supprimée - on utilise directement getRouterName()
// qui retourne les strings du JSON sans conversion inutile

/**
 * Obtient la marque depuis la configuration
 */
export function getBrandFromConfig(hostname?: string): string {
  return getDomainConfig(hostname).brand;
}

/**
 * Vérifie si on est en développement
 */
export function isDevelopment(hostname?: string): boolean {
  if (!hostname) {
    hostname = window.location.hostname;
  }

  return hostname === 'localhost' ||
         hostname === '127.0.0.1' ||
         hostname.includes('dev') ||
         hostname.includes('-dev');
}

/**
 * Obtient tous les noms de routeurs disponibles
 */
export function getAvailableRouterNames(): Record<string, string> {
  return domainConfig.routerNames;
}

/**
 * Obtient tous les domaines configurés
 */
export function getAllDomains(): Record<string, DomainConfig> {
  return domainConfig.domains;
}